// Trigger CI/CD rebuild - Nov 3 2025
import Button from "components/Button/Button";
import { useState } from "react";
import styles from "./CurrentRoute.module.scss";
import { ReactComponent as DraggableIcon } from "assets/draggable.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { store } from 'stores/Store';
import { Tab } from "Modules/MapRouter";
import React from "react";
import useExternalApiService from "helpers/ExternalApiService";
import SearchModal from "./components/SearchModal";
import { ReactComponent as HouseIcon } from "assets/house.svg";
import { ReactComponent as CloseIcon } from "assets/xCircle.svg";
import { ReactComponent as FlagIcon } from "assets/flag.svg";
import { date2usDotDate } from "helpers";
import MeetingModal from "./components/MeetingModal";
import StartTimeModal from "./components/StartTimeModal";
import Toastr from "components/Toastr";
import { decode } from "@googlemaps/polyline-codec";
import RoutesTab from "Modules/PractitionerBaseballCard/components/RoutesTab";
import { Group, Stack, Drawer, Box, CloseButton, Title, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DatePicker, DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import RouteCalendarExport from "./components/RouteCalendarExport";

interface IMapRouterProps {
  setSelectedTab: (tab: Tab) => void;
  setSelectedView: (view: string) => void;
  currentRouteName: string;
  currentRoute: any;
  clearCurrentRoute: () => void;
  setHasRouteChanged: (hasRouteChanged: boolean) => void;
  setShowUnsavedChangesModal: (showUnsavedChangesModal: boolean) => void;
  hasRouteChanged: boolean;
  showUnsavedChangesModal: boolean;
  clickedClear: boolean;
  setClickedClear: (clickedClear: boolean) => void;
  onRouteChange: (v: number) => void;
  onRouteSave: (id?: number) => void;
  setRouteName: (routeName: string) => void;
}

interface Waypoint {
  travelTime?: number;
  id?: number;
  address?: string;
  latitude?: string;
  longitude?: string;
  isFirstLocation?: boolean;
  isLastLocation?: boolean;
  start_time?: string;
  duration?: string;
  end_time?: string;
  arriveTime?: string;
  name?: string;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface PlacesResponse {
  success: boolean;
  error_msg: string;
  places: PlaceSuggestion[];
}
const parseStartTime = (startTime: string) => {
  if (!startTime) return "";
  if (startTime.includes("AM") || startTime.includes("PM")) {
    const [time, period] = startTime.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
      
    return `${year}-${month}-${day} ${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  }
  const date = new Date(startTime);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
}
const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to convert datetime to API format (ISO format)
const convertToApiFormat = (dateTimeStr: string) => {
  if (!dateTimeStr) return "";
  
  // If already in ISO format, return as is
  if (dateTimeStr.includes('T') && dateTimeStr.includes('Z')) {
    return dateTimeStr;
  }
  
  // If space-separated format, convert to ISO
  if (dateTimeStr.includes(' ') && dateTimeStr.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
    const date = new Date(dateTimeStr);
    return date.toISOString();
  }
  
  // If 12-hour format, convert to ISO
  if (dateTimeStr.includes("AM") || dateTimeStr.includes("PM")) {
    const [time, period] = dateTimeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    const date = new Date(`${year}-${month}-${day} ${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
    return date.toISOString();
  }
  
  // If ISO format without Z, add Z
  if (dateTimeStr.includes('T')) {
    const date = new Date(dateTimeStr);
    return date.toISOString();
  }
  
  return dateTimeStr;
};
const CurrentRoute = ({ setSelectedTab, setSelectedView, currentRouteName, currentRoute, onRouteSave, clearCurrentRoute,
                        setHasRouteChanged, setShowUnsavedChangesModal, hasRouteChanged, showUnsavedChangesModal,
                        clickedClear, setClickedClear, onRouteChange, setRouteName,  }: IMapRouterProps) => {
  const [waypoints, setWaypoints] = useState<(Waypoint | null)[]>([null, null]);
  const [selectedTime, setSelectedTime] = useState('10:00');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFirstLocation, setIsFirstLocation] = useState(true);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedWaypoint, setSelectedWaypoint] = useState<any>(waypoints[1]);
  const [isDragging, setIsDragging] = useState(false);
  const [clickTime, setClickTime] = useState(new Date());
  const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
  const [firstStartTime, setFirstStartTime] = useState(formatTime(new Date()));
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const lastOptimizedWaypointsRef = React.useRef<string>("");
  const [saveRouteDrawerOpened, { open: openSaveRouteDrawer, close: closeSaveRouteDrawer }] = useDisclosure(false);
  const [routeDate, setRouteDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [tempRouteName, setTempRouteName] = useState(currentRoute?.name || currentRouteName);
  const [calendarAddedMap, setCalendarAddedMap] = useState<Map<number, boolean>>(new Map());

  React.useEffect(() => {
    if (clickedClear && !showUnsavedChangesModal) {
      setShowUnsavedChangesModal(false);
      setClickedClear(false);
      store.setSelectedPractitioners([]); 
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      store.setCurrentDirections([]);
    }
  }, [showUnsavedChangesModal, clickedClear]);
  React.useEffect(() => {
    console.log("hasRouteChanged", hasRouteChanged);
  }, [hasRouteChanged]);

  React.useEffect(() => {
    if (currentRoute?.name) {
      setTempRouteName(currentRoute.name);
    }
  }, [currentRoute]);

  // Load calendar states for waypoints when route loads
  React.useEffect(() => {
    const loadCalendarStates = async () => {
      if (!routeDate || !waypoints.length) {
        return;
      }

      const newMap = new Map<number, boolean>();

      for (const waypoint of waypoints) {
        // Skip first/last locations and waypoints without IDs or start times
        if (!waypoint || waypoint.isFirstLocation || waypoint.isLastLocation || !waypoint.id || !waypoint.start_time) {
          continue;
        }

        try {
          // Convert start_time to 24-hour format for backend
          const convert12HourTo24Hour = (time12h: string): string => {
            if (!time12h || time12h.trim() === '') return '';

            // If already in HH:MM format (24-hour), return as is
            if (/^\d{2}:\d{2}$/.test(time12h)) {
              return time12h;
            }

            // If in ISO datetime format
            if (time12h.includes('T') && time12h.includes(':')) {
              const timePart = time12h.split('T')[1];
              if (timePart) {
                return timePart.substring(0, 5);
              }
            }

            // If in space-separated datetime format
            if (time12h.includes('-') && time12h.includes(':') && time12h.includes(' ')) {
              const timePart = time12h.split(' ')[1];
              if (timePart) {
                return timePart.substring(0, 5);
              }
            }

            // Handle 12-hour format
            const parts = time12h.split(' ');
            if (parts.length < 2) return '';

            const [time, period] = parts;
            if (!time || !time.includes(':')) return '';

            const timeParts = time.split(':');
            const hours = parseInt(timeParts[0]);
            const minutes = parseInt(timeParts[1]);

            if (isNaN(hours) || isNaN(minutes)) return '';

            const isPM = period === 'PM';
            const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
            return `${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
          };

          const startTime24h = convert12HourTo24Hour(waypoint.start_time);

          // Backend uses DATE(follow_up_date) in SQL query, so we only need to send the date portion
          // The backend query: WHERE DATE(follow_up_date) = %s
          const followUpDateOnly = routeDate;  // Just send "YYYY-MM-DD"

          const response = await postData('/api/e/call_get_calendar_confirmation', {
            practitioner_id: waypoint.id,
            follow_up_date: followUpDateOnly
          });

          if (response?.calendar_added) {
            newMap.set(waypoint.id, true);
          }
        } catch (error) {
          console.error('Failed to load calendar state for waypoint:', waypoint.id, error);
        }
      }

      setCalendarAddedMap(newMap);
    };

    loadCalendarStates();
  }, [routeDate, waypoints]);


  const formatDateTimeToAMPM = (dateTimeStr: string) => {
    if (!dateTimeStr) return "";
    
    // Handle both ISO datetime strings (2025-10-17T20:50:00) and space-separated datetime strings
    let time: string;
    if (dateTimeStr.includes('T')) {
      // ISO format: extract time part after 'T'
      time = dateTimeStr.split('T')[1];
    } else {
      // Space-separated format: extract time part after space
      time = dateTimeStr.split(' ')[1];
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    // Return formatted time
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  }

  const handleRoutes = (opt: string) => {
    switch (opt) {
      case 'google':
        OpenMapsNavigation()
        break;
      case 'waze':
        OpenWazeNavigation()
        break;
      case 'apple':
        OpenAppleMapsNavigation()
        break;
    }
    setIsGoRouteModalOpen(false)
  }

  // Handle removing calendar invite
  const handleRemoveCalendarInvite = async (practitionerId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent waypoint click

    if (!currentRoute?.id) return;

    try {
      const response = await postData('/api/remove_calendar_invite', {
        route_id: currentRoute.id,
        practitioner_id: practitionerId
      });

      if (response.success) {
        // Update local state
        const newMap = new Map(calendarAddedMap);
        newMap.delete(practitionerId);
        setCalendarAddedMap(newMap);
      }
    } catch (error) {
      console.error('Failed to remove calendar invite:', error);
    }
  }



  const OpenMapsNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);
    
    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Build Google Maps URL for the current waypoint only
    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedWaypoint.latitude},${selectedWaypoint.longitude}`;
    // Open in new tab
    window.open(url, '_blank');
  }
  const OpenWazeNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);

    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Navigate to destination directly (iOS-compatible)
    const url = `https://waze.com/ul?ll=${selectedWaypoint.latitude},${selectedWaypoint.longitude}&navigate=yes`;
    window.open(url, '_blank');
  }

  const OpenAppleMapsNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);

    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Navigate to destination directly (iOS-compatible)
    const url = `maps://?daddr=${selectedWaypoint.latitude},${selectedWaypoint.longitude}`;
    console.log('Apple Maps url', url);
    window.open(url, '_blank');
  }

  React.useEffect(() => {
    onRouteChange(waypoints?.filter((w) => w)?.length || 0);
  }, [waypoints])

  React.useEffect(() => {
    console.log("first useEffect");
    firstStartTimeUpdate(parseStartTime(store.selectedPractitioners[0]?.start_time));
    if(currentRoute?.practitioner_ids && store.selectedPractitioners.length === 0) {
      const getPractitioners = async () => {
        const response = await postData('/api/get_multiple_practitioners_by_ids', {
          practitioner_ids: currentRoute.practitioner_ids
        });
        const practitioners = response.practitioners || [];
        let firstLocation: any = null;
        let lastLocation: any = null;

        if (currentRoute.locations[0].is_starting_location == "1") {
          firstLocation = {
            address: currentRoute.locations[0].address,
            latitude: currentRoute.locations[0].latitude,
            longitude: currentRoute.locations[0].longitude,
            isFirstLocation: true,
            start_time: currentRoute.locations[0]?.start_time
          }
        } 
        if (currentRoute.locations[currentRoute.locations.length - 1].is_ending_location == "1") {
          lastLocation = {
            address: currentRoute.locations[currentRoute.locations.length - 1].address,
            latitude: currentRoute.locations[currentRoute.locations.length - 1].latitude,
            longitude: currentRoute.locations[currentRoute.locations.length - 1].longitude,
            isLastLocation: true
          }
        }
        // Update both waypoints and store
        const startedWaypoints = [firstLocation, ...(
            practitioners
              .map((p: any, practitionerIndex: number) => {
                const defaultAddress = p.addresses.find((a: any) => a.id === p.default_address);
                // Calculate the actual index in currentRoute.locations
                // If there's a firstLocation, practitioner 0 maps to location 1
                const locationIndex = firstLocation ? practitionerIndex + 1 : practitionerIndex;
                const routeLocation = currentRoute.locations[locationIndex];

                return {
                  ...defaultAddress, // Keep address info for display
                  ...p, // Keep practitioner info
                  // CRITICAL: Use coordinates from the route, not from the practitioner's address
                  latitude: routeLocation.latitude,
                  longitude: routeLocation.longitude,
                  address: routeLocation.address, // Use route's address as source of truth
                }
              })
        ), lastLocation].map((waypoint: any, index: number) => {
          console.log("waypointx",waypoint)
          if(waypoint) {
            waypoint.start_time = currentRoute.locations[index].start_time;
            waypoint.duration = parseInt(currentRoute.locations[index].duration);
            return waypoint;
          }
          return null; // Explicitly return null when waypoint is falsy
        });
        console.log("startedWaypoints",startedWaypoints)
        setWaypoints(startedWaypoints.filter(wp => wp !== null));

        store.setSelectedPractitioners(startedWaypoints.filter(wp => wp !== null));

        // Set route date if available
        if (currentRoute.route_date) {
          setRouteDate(currentRoute.route_date);
        }
      };
      getPractitioners();
    }else{
     setWaypoints(store.selectedPractitioners);
    }
  }, [currentRoute]);

  // Add useEffect to update waypoints when selectedPractitioners changes
  React.useEffect(() => {
    // Only run this effect if currentRoute is already loaded and we have selectedPractitioners
    if (store.selectedPractitioners.length > 0 && !currentRoute?.is_loading) {
 
      let firstLocation: any = null;
      let lastLocation: any = null;
      if (store.selectedPractitioners[0]?.isFirstLocation) {
        firstLocation = {
          address: store.selectedPractitioners[0]?.address,
          latitude: store.selectedPractitioners[0]?.latitude,
          longitude: store.selectedPractitioners[0]?.longitude,
          isFirstLocation: true,
          travelTime: store.selectedPractitioners[0].travelTime,
          start_time: store.selectedPractitioners[0]?.start_time
        }
      } 
      if (store.selectedPractitioners[store.selectedPractitioners.length - 1]?.isLastLocation) {
        lastLocation = {
          address: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.address,
          latitude: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.latitude,
          longitude: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.longitude,
          isLastLocation: true,
          arriveTime: store.selectedPractitioners[store.selectedPractitioners.length - 1].arriveTime
        }
      }
      // Update waypoints with current first/last locations and all practitioners
      console.log("firstLocation",firstLocation)
      setWaypoints([firstLocation, ...store.selectedPractitioners.filter(p => !p?.isFirstLocation && !p?.isLastLocation), lastLocation]);
    }
  }, [store.selectedPractitioners, currentRoute]);

  // Add useEffect to initialize times when practitioners change
  React.useEffect(() => {
    
    const wp = waypoints.filter(wp => wp !== null);
    const waypointsKey = JSON.stringify(wp.map(w => ({ id: w?.id, lat: w?.latitude, lng: w?.longitude })));
    
    if (wp.length >= 2 && !isOptimizing && lastOptimizedWaypointsRef.current !== waypointsKey) {
      console.log("optimizing route");
      setIsOptimizing(true);
      lastOptimizedWaypointsRef.current = waypointsKey;
      onOptimizeRoute(false);
    } else if (wp.length < 2) {
      console.log("not optimizing route");
    }
    // else if (wp.length > 25) {
    //   console.log("not optimizing route");
    //   setToastMessage("You can only have 25 practitioners on a route");
    //   setShowToast(true);
    // }

  }, [waypoints, isOptimizing]); 

  const handleSearchClick = (isFirst:boolean) => {
    setIsFirstLocation(isFirst);
    setIsSearchModalOpen(true);
  }

  const handleDragStart = () => {
    setIsDragging(true);
    setClickTime(new Date());
  };


  const renderSearchModal = () =>{
    return(
      <SearchModal
      isOpen={isSearchModalOpen}
      onClose={handleModalClose}
      searchTerm={searchTerm}
      suggestions={suggestions}
      onSuggestionClick={handleFirstLastLocation}
      firstLocation={isFirstLocation}
      onSearchChange={handleSearchChange}
    />
    )
  }
  const checkDuration = () => {
    if(!currentRoute){
      const currentDuration = waypoints.find((waypoint: any) => waypoint?.id === selectedWaypoint?.id)?.duration;
      if (currentDuration) {
        if (parseInt(currentDuration) < 60) {
          return `${currentDuration} mins`;
        } else {
          const hours = parseInt(currentDuration) / 60;
          return `${hours} hours`;
        }
      }
      return '15 mins';
    }
    const currentDuration = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.duration;
    if (!currentDuration) return '15 mins';
    if (currentDuration < 60) {
      return `${currentDuration} mins`;
    } else {
      const hours = currentDuration / 60;
      return `${hours} hours`;
    }
  }

  const checkStartTime = () => {
    if(!currentRoute){
      const currentStartTime = selectedWaypoint?.start_time;
      if (currentStartTime) {
        return formatDateTimeToAMPM(currentStartTime);
      }
      return '';
    }
    const currentStartTime = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.start_time;

    return currentStartTime?formatDateTimeToAMPM(currentStartTime):'';
  }

  const parseLength = (length: number) => {
    if (!length) return '15m';
    if (length < 60) {
      return `${length} m`;
    } else {
      const hours = length / 60;
      return `${hours} h`;
    }
  }
  const renderMeetingModal = () => {
    if (!selectedWaypoint) return null;
    return (
      <MeetingModal
        isOpen={isMeetingModalOpen}
        practitioner={selectedWaypoint}
        onClose={() => {
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        updatePractitioner={updatePractitioner}
        onDelete={() => {
          handlePractitionerRemoval(selectedWaypoint);
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        setSelectedView={setSelectedView}
        checkDuration={checkDuration}
        checkStartTime={checkStartTime}
        setGoRouteModalIsOpen={setIsGoRouteModalOpen}
      />
    );
  }

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    const response = await getPlacesAutocomplete(value) as PlacesResponse;
    if (response.success) {
      setSuggestions(response.places);
    }
  }

    // get latitude and longitude from location
    const getPlaceDetails = async (placeId: string) => {
      try {
        const result = await postData('/api/google_places_details', {
          place_id: placeId
        });
        if (result.success) {
          return {
            latitude: result.place.geometry.location.lat,
            longitude: result.place.geometry.location.lng
          };
        }
        throw new Error(result.error_msg || 'Failed to get place details');
      } catch (error) {
        console.error('Error getting place details:', error);
        throw error;
      }
    };

  const handleFirstLastLocation = async (place: any, isFirstLocation: boolean, currentLocation: boolean) => {
    let location: any;
    try {
      if(currentLocation) {
        location = {
          address: "Current Location",
          latitude: place.geometry.location.lat,
          longitude: place.geometry.location.lng
        };
      } else {
        const { latitude, longitude } = await getPlaceDetails(place.place_id);
        location = {
          address: place.description,
          latitude,
          longitude
        };
      }
      if(isFirstLocation) {
        setWaypoints(prev => {
          let newWaypoints;
          if (prev[0] == null) {
            newWaypoints = [{...location, isFirstLocation: true}, ...prev.slice(1)];
          } else {
            newWaypoints = [{...location, isFirstLocation: true}, ...prev];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          setFirstStartTime(formatTime(new Date()));
          return newWaypoints;
        });
      } else {
        setWaypoints(prev => {
          let newWaypoints;
          if (prev[prev.length - 1] == null) {
            newWaypoints = [...prev.slice(0, -1), {...location, isLastLocation: true}];
          } else {
            newWaypoints = [...prev, {...location, isLastLocation: true}];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          return newWaypoints;
        });
      }
      setSearchTerm('');
      setSuggestions([]);
      setIsSearchModalOpen(false);
    } catch (error) {
      console.error('Error setting location:', error);
    }
  }

  const handlePractitionerRemoval = (practitioner: any) => {
    store.removeFromRoute(practitioner);
    setWaypoints(prev => {
      const newWaypoints = prev.filter(wp => wp?.id !== practitioner?.id);
      store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
      setHasRouteChanged(true);
      return newWaypoints;
    });
  };

  const handleModalClose = () => {
    setIsSearchModalOpen(false);
    setSuggestions([]);
  }
  const updatePractitioner = async (practitionerId: number, updateData: any) => {
    // Update the waypoint in the local state while preserving order
    const updatedWaypoints = waypoints.map(wp => {
      if (wp === null) return wp;
      const {arriveTime, ...wpWithoutArriveTime} = wp;
      return wp.id === practitionerId 
        ? { ...wpWithoutArriveTime, duration: updateData.meeting_duration, start_time: updateData.start_time ? parseStartTime(updateData.start_time) : ""}
        : wpWithoutArriveTime;
    });
    store.setSelectedPractitioners(updatedWaypoints.filter(wp => wp !== null));
    setWaypoints(updatedWaypoints);
    
    // Update store with the same order as waypoints
  };

  const displayTime = (waypoint: any) => {
    // Check if there's a real user-set start_time (not null, not empty string, not "null" string)
    const hasValidStartTime = waypoint.start_time &&
                              waypoint.start_time !== "" &&
                              waypoint.start_time !== "null" &&
                              waypoint.start_time !== null;

    if(hasValidStartTime) {
      return parseStartTime(waypoint.start_time);
    }

    // Don't show arriveTime at all - return empty string instead
    return "";
  }
  const SortableItem = ({ id, waypoint, arriveTime, index, onClick }: { id: number, waypoint: any, arriveTime: string, index: number, onClick: () => void }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    const notNullWaypoint = waypoints.filter(wp => wp !== null);
    return (
      <div
        ref={setNodeRef} 
        style={style}
        onClick={() => {
          setSelectedWaypoint(waypoint);
          setIsMeetingModalOpen(true);
        }}
      >
        <div key={waypoint?.id}>
          <div className={styles.waypointSubWrapper}>
            <div
              key={waypoint?.id} className={styles.waypointWapper}>
              <DraggableIcon 
                {...attributes}
                {...listeners}
                style={{
                  cursor: "grab",
                  touchAction: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  WebkitTouchCallout: "none",
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation(); 
                }}
              />
              <div className={styles.waypointInfo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <p className={styles.headerText}>{index + (waypoints[0]? 2 : 1)}. {waypoint?.name || waypoint?.primary_name || ""}</p>
                  {waypoint?.id && calendarAddedMap.get(waypoint.id) && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#4CAF5020',
                      border: '1px solid #4CAF50',
                      borderRadius: '12px',
                      padding: '2px 8px',
                      fontSize: '10px',
                      fontWeight: 500,
                      color: '#2E7D32'
                    }}>
                      <span>✓ Added to Calendar</span>
                    </div>
                  )}
                </div>
                <div className={styles.travelTextContainer}>
                  <p className={styles.timeText}>{displayTime(waypoint)}</p>
                  <div className={styles.travelLengthContainer}>
                    Length:
                    <p className={styles.travelLengthText}>{parseLength(waypoint?.duration)}</p>
                  </div>
                </div>
              </div>
              <div className={styles.expandIconContainer}>
                <ExpandIcon width={16} height={16} />
              </div>
            </div>
            {!isNaN(waypoint?.travelTime) && waypoint.id !== notNullWaypoint[notNullWaypoint.length - 1]?.id  && (
              <div className={styles.custonSplitter}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>Travel Time: {waypoint.travelTime} minutes</p>
                </div>
              </div>
            )}
          </div>

          </div>
      </div>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if(clickTime && new Date().getTime() - clickTime.getTime() < 100) {
      setSelectedWaypoint(waypoints[active?.id]);
      setIsMeetingModalOpen(true);
    }
    if (active?.id !== over?.id) {
      
      const oldIndex = active?.id;
      const newIndex = over?.id;
      setHasRouteChanged(true);
      const reorderedWaypoints = arrayMove(waypoints, oldIndex, newIndex);
      store.setSelectedPractitioners(reorderedWaypoints.filter(wp => wp !== null));
      console.log("reorderedWaypoints", reorderedWaypoints);
    }
    setTimeout(() => setIsDragging(false), 0);
  };

  const onClearLocations = (options?: { bypassModal?: boolean }) => {
    const shouldBypassModal = options?.bypassModal ?? false;

    if (hasRouteChanged && !shouldBypassModal) {
      setShowUnsavedChangesModal(true);
      setClickedClear(true);
    } else {
      store.setSelectedPractitioners([]);
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      setClickedClear(false);
      store.setCurrentDirections([])
    }
  };

  const onSaveRoute = async (routeId?: number) => {
    setRouteName(tempRouteName);
    let newRouteId;
    if(routeId) {
      await updateRoute();
    } else {
      newRouteId = await saveRoute();
    }

    if (currentRoute || newRouteId) {
      onRouteSave(newRouteId ||currentRoute?.id );
      setHasRouteChanged(false); 
      onClearLocations({ bypassModal: true }); // Call with option to bypass modal
      setSelectedTab(Tab.SAVED_ROUTES);
    } 
  }

  const handleFirstLocationStartTime = () => {
    setSelectedWaypoint(waypoints[0]);
    setIsStartTimeModalOpen(true);
  }

  const saveRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any) => waypoint?.start_time || "");
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": tempRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
      "date": routeDate,
    }

    if (waypoints && waypoints.length > 0) {
      const { route_id } =await postData('/api/add_route', {
        route_data: routeData,
        practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
      });
      return route_id;
    }
    return 0;
  }

  const updateRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any) => waypoint?.start_time || "");
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": tempRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
      "date": routeDate,
    }
    await postData('/api/update_route', {
      route_id: currentRoute?.id,
      route_data: routeData,
      practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
    });
  }
  const onOptimizeRoute = async (waypointOrder?: boolean) => {
    
    await onOptimizeRouteTours(waypointOrder);
    return
    
  }


  // ——— add at top if you don't already have it —————————————
  interface LatLng {
    lat: number;
    lng: number;
    name?: string;
}

interface RouteResponse {
  routes: Array<{
    beforeDepartureIndex?: Array<number>;
    skippedIntermediateWaypointIndex?: Array<number>;
    visits?: Array<{ startTime: string, duration: string }>;
    transitions?: Array<{ travelDuration: string }>;
    optimizedIntermediateWaypointIndex?: number[];
    legs: Array<{ duration: string, startTime: string }>;
    routePolyline?: { points: string };
    summary: {
      hasTrafficInfeasibilities: boolean;
      totalDuration: string;
      travelDuration: string;
      visitDuration: string;
      totalDistanceMeters: number;
      performedShipmentCount: number;
    };
  }>;
}

async function onOptimizeRouteTours(waypointOrder?: boolean): Promise<void> {
  // if(waypoints.length > 25) {
  //   setToastMessage("You cannot optimize a route with more than 25 practitioners");
  //   setShowToast(true);
  //   return;
  // }
  // 1) UI spin

  // 2) Start & end LatLng
  let startLatLng = null;
  if(waypoints[0] !== null){
    startLatLng = { lat: +waypoints[0]!.latitude!, lng: +waypoints[0]!.longitude! };
  }
      
  let endLatLng = null;
  if(waypoints[waypoints.length - 1] !== null){
    endLatLng = { lat: +waypoints[waypoints.length - 1]!.latitude!, lng: +waypoints[waypoints.length - 1]!.longitude! };
  }
  // 3) Middle waypoints for the API
  const waypointData: LatLng[] = waypoints
    .filter(wp => wp !== null)
    .filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation)
    .map((wp) => ({
      name: wp!.name,
      lat: +wp!.latitude!,
      lng: +wp!.longitude!,
      startTime: wp!.start_time ? convertToApiFormat(wp!.start_time) : "",
      duration: wp!.duration
    }))
  console.log('waypointData', waypointData)
  // 4) Compute initialLocalTime (same logic as onOptimizeRoute)
  let initialLocalTime: Date;
  const firstLocation = waypoints.find(wp => wp?.isFirstLocation);
  if (firstLocation && firstLocation.start_time) {
    // Use route date if available, otherwise use today's date
    {
      const dateToUse = routeDate ? new Date(routeDate) : new Date();
      const firstLocationTime = new Date(convertToApiFormat(firstLocation.start_time));
      initialLocalTime = new Date(
        dateToUse.getFullYear(),
        dateToUse.getMonth(),
        dateToUse.getDate(),
        firstLocationTime.getHours(),
        firstLocationTime.getMinutes(),
        firstLocationTime.getSeconds() // use seconds as well for full ISO parity
      );
    }
    console.log('firstLocation', firstLocation)
    console.log('initialLocalTime 1', convertToApiFormat(firstLocation.start_time))
  }
  else if (waypoints[0] !== null && firstStartTime) {
    initialLocalTime = new Date(parseStartTime(firstStartTime));
    console.log('initialLocalTime 2', initialLocalTime)
  } else if (waypoints[1]?.start_time) {
    initialLocalTime = new Date(waypoints[1]!.start_time!);
    console.log('initialLocalTime 3', initialLocalTime)
  } else {
    initialLocalTime = new Date();
    console.log('initialLocalTime 4', initialLocalTime)
  }
  if(waypoints.filter(wp=> wp?.isFirstLocation).length === 0){
    // Find the waypoint with the earliest start_time among non-first/last location waypoints
    const waypointsWithStartTime = waypoints
      .filter(wp => wp !== null && !wp.isFirstLocation && !wp.isLastLocation && wp.start_time)
      .sort((a, b) => a!.start_time!.localeCompare(b!.start_time!));
    if (waypointsWithStartTime.length > 0) {
      initialLocalTime = new Date(new Date(waypointsWithStartTime[0]!.start_time!).getTime() - 1);
    }else{
      initialLocalTime = new Date(initialLocalTime.getTime() - 60 * 1000);
    }
  }
  
  if (currentRoute?.route_date) {
    const routeDate = new Date(currentRoute.route_date);
    const routeYear = routeDate.getFullYear();
    const routeMonth = routeDate.getMonth();
    const routeDay = routeDate.getDate();
    
    // Create new date with route's date but keep the time from initialLocalTime
    initialLocalTime = new Date(routeYear, routeMonth, routeDay, 
      initialLocalTime.getHours(), initialLocalTime.getMinutes(), initialLocalTime.getSeconds());
  }
 
  const departureTimeUtc = initialLocalTime.toISOString();
  try {
    // 5) Hit the Tours endpoint

    
    const endTime = new Date(new Date(departureTimeUtc).getTime() + Math.max(124, waypoints.length *0.8) * 60 * 60 * 1000).toISOString();
    const res = await postData(
      '/api/google_routes_optimization_tours',
      {
        origin: startLatLng,
        destination: endLatLng,
        waypoints: waypointData,
        departureTime: departureTimeUtc,
        optimizeWaypointOrder: waypointOrder,
        endTime: endTime,
      }
    ) as {
      beforeDepartureIndex: any; data: RouteResponse 
};

    const route = res.data.routes[0];
    if(route.beforeDepartureIndex?.length! > 0) {
      const practitionerNames = route.beforeDepartureIndex?.map((index) => waypointData[index].name).join(', ')
      setToastMessage(`Time on ${practitionerNames} is set previous to departure time.`);
      setIsOptimizing(false);
      setShowToast(true);
      return
    }

    // Decode polyline and set route coordinates for drawing
    const _currentDirections: number[][] = [];
    
    // Check for the new routePolyline structure first
    if (route?.routePolyline?.points) {
      const encodedPolyline = route.routePolyline.points;
      const decodedPolyline = decode(encodedPolyline);
      _currentDirections.push(
        ...(decodedPolyline.map(([lat, lon]) => {
          return [lon, lat]
        }))
      );
    }

    
    store.setCurrentDirections(_currentDirections);
    if(route.skippedIntermediateWaypointIndex?.length! > 0 || route.summary.hasTrafficInfeasibilities) {
      console.log('skippedIntermediateWaypointIndex', route.skippedIntermediateWaypointIndex)
      setShowToast(true);
      setToastMessage(`Due to traffic conditions, the following locations with required start times are not possible: ${route.skippedIntermediateWaypointIndex?.map((index) => waypointData[index].name).join(', ')}.  Please remove the start time requirements in order to optimize your route.`);
      setIsOptimizing(false);
      return
    }
    // ─── 6) Normalize into order + legs ─────────────────────────────
    let order: number[];
    let visits: Array<{startTime: string, duration: string }>;
    let legs: Array<{ duration: string, startTime: string }> | undefined;

    if (Array.isArray(route.optimizedIntermediateWaypointIndex) && Array.isArray(route.visits)) {
      // ComputeRoutes‐style (fallback)
      order = route.optimizedIntermediateWaypointIndex;
      visits  = route.visits.map((l) => ({ startTime: l.startTime, duration: l.duration }));
      legs = route.legs.map((t) => ({ duration: t.duration, startTime: t.startTime }));
    } else {
      throw new Error('Unexpected route shape');
    }
    // ────────────────────────────────────────────────────────────────

    // 7) Rebuild optimized waypoints array
    const firstWaypoint = waypoints.filter(wp => wp?.isFirstLocation);
    const lastWaypoint = waypoints.filter(wp=> wp?.isLastLocation);
    const middle = waypoints.filter(wp=> wp !== null).filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation);
    
    // Create deep copies to preserve original start_time values
    const optimizedMiddle = order.map((idx: number) => {
      const originalWaypoint = middle[idx]!;
      return {
        ...originalWaypoint,
        // Explicitly preserve the original start_time constraint
        start_time: originalWaypoint.start_time
      };
    });
    
    let optimizedWaypoints: Array<Waypoint | null> = [
      ...firstWaypoint.map(wp => ({ ...wp, start_time: wp?.start_time })), // Preserve first location start_time
      ...optimizedMiddle,
      ...lastWaypoint
    ];
    optimizedWaypoints = optimizedWaypoints.filter(wp => wp !== null);
    // 8) Compute travelTime & arriveTime
    let clock = new Date(initialLocalTime.getTime());


    // subsequent legs → travel + service
    let legsTrueSize = legs.length;
    let visitPadding = 0;
    let legsStartIndex = 0;
    let legsPadding = 0;
    if(firstWaypoint.length > 0){ // if dedicated starting point, set paddings to calculattion indexes
      visitPadding = 1;
    }else{ 
      legsPadding = 1;
    }

    for (let i = 0; i < visits.length; i++) {
      const travelMin = Math.round(parseInt(legs[i + legsPadding].duration, 10) / 60);
      optimizedWaypoints[i]!.travelTime = travelMin;
      optimizedWaypoints[i + visitPadding]!.arriveTime = formatTime(new Date(visits[i]!.startTime));
    }
    if(lastWaypoint.length > 0){ // if dedicated ending point, manually calculate for it
      // Use the last leg's start time + duration to get arrival time at final destination
      let lastLegStartTime = new Date(legs[legs.length - 1]!.startTime);
      let lastLegDurationMinutes = Math.round(parseInt(legs[legs.length - 1]!.duration, 10) / 60);
      lastLegStartTime.setMinutes(lastLegStartTime.getMinutes() + lastLegDurationMinutes);
      optimizedWaypoints[optimizedWaypoints.length - 1]!.arriveTime = formatTime(lastLegStartTime);
    }
    // 9) Commit to state

    const wp = optimizedWaypoints.filter(wp => wp !== null);
    lastOptimizedWaypointsRef.current = JSON.stringify(wp.map(w => ({ id: w?.id, lat: w?.latitude, lng: w?.longitude })));
    
    setWaypoints(optimizedWaypoints);
    const finalWps = optimizedWaypoints.filter(
      (wp): wp is Waypoint => wp !== null
    );
    store.setSelectedPractitioners(finalWps);
    setHasRouteChanged(true);

  } catch (err) {
    console.error('Error optimizing via Tours:', err);
  }
  setIsOptimizing(false);
}

  const getPlacesAutocomplete = async (input: string) => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const currentLocationString = `${lat},${lng}`;
          
          try {
            const result = await postData('/api/google_places_autocomplete', {
              query: input,
              location: currentLocationString,
            });
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, (error) => {
          reject(error);
        });
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  const firstStartTimeUpdate = (startTime: string) => {
      const firstWaypoint = waypoints.filter(wp => wp !== null)[0];
      console.log("firstWaypoint",firstWaypoint)
      setFirstStartTime(startTime);
      if (firstWaypoint) {
        firstWaypoint.start_time = startTime ? parseStartTime(startTime) : (firstStartTime ? firstStartTime : "");
        setWaypoints([firstWaypoint, ...waypoints.slice(1)]);
        store.setSelectedPractitioners([firstWaypoint, ...store.selectedPractitioners.slice(1)]);
     
      }
  }
  return (
    <div className={styles.currentRouteContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.btnsContainer}>
          {/*<Button type="outline" onClick={() => onOptimizeRoute(true)}>OPTIMIZE</Button>*/}
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                onOptimizeRouteTours(true);
                setIsOptimizing(true);
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" >OPTIMIZE</Button>
          </div>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                setSelectedTab(Tab.LIST_CUSTOMERS)
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" >ADD</Button>
          </div>
        </div>
        <div className={styles.btnsContainer}>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                onClearLocations()
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" color="#E64B3780" textColor="#E64B37">NEW ROUTE</Button>
          </div>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                openSaveRouteDrawer();
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" color="#4F7D9C" textColor="white">SAVE</Button>
          </div>
        </div>
      </div>

      {renderSearchModal()}
      {renderMeetingModal()}
      <StartTimeModal 
        isOpen={isStartTimeModalOpen} 
        onClose={() => setIsStartTimeModalOpen(false)} 
        startTime={firstStartTime} 
        setStartTime={firstStartTimeUpdate} 
        setIsStartTimeModalOpen={setIsStartTimeModalOpen}
      />
      <div className={styles.searchContainer}>
        {!waypoints[0] ? (waypoints.length > 2 ? (
          
          <div className={styles.searchInput} onClick={()=>handleSearchClick(true)}>
            <HouseIcon width={'25px'} height={'25px'} style={{marginRight: '15px',}}/>
            <span className={styles.placeholder}>Starting location</span>
          </div>
        ): null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>1.</div>
              <span className={styles.locationAddress}>{waypoints[0].address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setFirstStartTime("");
                setWaypoints(prev => [null, ...prev.slice(1)]);
                setHasRouteChanged(true);
              }}/>
              <div onClick={() => handleFirstLocationStartTime()} className={styles.timeText}>
                {firstStartTime ? (firstStartTime) : formatTime(new Date())}
              </div>
            </div>
            {waypoints[0]?.travelTime ? (
              <div className={`${styles.custonSplitter} ${styles.firstLocationSplitter}`}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>Travel Time: {waypoints[0]?.travelTime} minutes</p>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={waypoints.slice(1, -1).map((_, index) => index+1)} strategy={verticalListSortingStrategy}>
            {waypoints.slice(1, -1).map((waypoint, index) => (
              <SortableItem 
                key={index+1} 
                id={index+1} 
                waypoint={waypoint} 
                arriveTime={waypoint?.arriveTime || ""}
                index={index}
                onClick={() => {}}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      {!waypoints[waypoints.length - 1] ? (waypoints.length > 2 ? (
          <div className={styles.searchInput} onClick={()=>handleSearchClick(false)}>
            <FlagIcon width={'20px'} height={'20px'} style={{paddingLeft: '4px', marginRight: '15px'}}/>
            <span className={styles.placeholder}>Ending location</span>
          </div>
        ) : null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>{store.selectedPractitioners.length}.</div>
              <span className={styles.locationAddress}>{waypoints[waypoints?.length - 1]?.address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setWaypoints(prev => [...prev.slice(0, -1), null]);
                setHasRouteChanged(true);
              }}/>
              <div className={styles.timeText}>
                {waypoints[waypoints.length - 1]?.arriveTime}
              </div>
            </div>
          </div>
        )}
      
      <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} />
      <Toastr closeTime={5000} backgroundColor={'#E64B37'} message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} />
      
      <Drawer
        opened={saveRouteDrawerOpened}
        onClose={closeSaveRouteDrawer}
        position="bottom"
        size="610px"
        radius="30px 30px 0 0"
        zIndex={5000}
        title={
          <Title 
            order={2} 
            fw={700}
            size={24}
            c="#4F7D9C"
            ff="Manrope, sans-serif"
          >
            Save Route
          </Title>
        }
        styles={{
          header: {
            borderBottom: "1px solid #E0E0E0",
            paddingBottom: "0px",
            paddingLeft: "0px",
            paddingRight: "0px",
            marginLeft: "30px",
            marginRight: "30px",
          }
        }}
      >
        <Box
          pl={25}
          pr={25}
          pt={10}
          pos="relative"
        >
           <Stack gap={'xs'}>
             <div className={styles.modalLabel}>Name</div> 
             <div className={styles.modalRouteNameHeader}>
               <input
                 type="text"
                 value={tempRouteName}
                 onChange={(e) => setTempRouteName(e.target.value)}
                 className={styles.modalRouteNameInput}
                 placeholder={"Enter a new Route Name"}
               />
                 {tempRouteName && (
                   <CloseIcon onClick={() => setTempRouteName('')} />
                 )}
             </div>

             <div className={styles.modalLabel}>Date</div>
             <DatePickerInput
               value={routeDate}
               onChange={setRouteDate}
               placeholder="Select date"
               valueFormat="MMMM, D, YYYY"
               firstDayOfWeek={0}
               popoverProps={{
                zIndex: 6000,
                styles: {
                  dropdown: {
                    border: '2px solid #E0E0E0'
                  }
                }
                
               }}
               styles={{
                 input: {
                   fontSize: '16px',
                   paddingBottom: '5px',
                   border: 'none',
                   borderBottom: '1px solid #E0E0E0',
                   backgroundColor: 'transparent',
                   borderRadius: '0',
                   padding: '0px',
                   '&:focus': {
                     borderBottom: '1px solid #4F7D9C',
                     boxShadow: 'none'
                   },
                   button: {
                    padding: '0px'
                   }

                 }
               }}
             />

             {/* Calendar Export Section */}
             <RouteCalendarExport
               routeDate={routeDate}
               meetings={waypoints}
             />
           </Stack>


        </Box>

        <div className={styles.saveRouteButtonContainer}>
            <button 
              className={styles.saveRouteButtonEnabled + " " + styles.renameRouteButton}
              disabled={currentRoute?.id ? false : true}
              onClick={() => {
                onSaveRoute(currentRoute?.id);
                closeSaveRouteDrawer();
              }}
            >
              Rename<br/>Existing Route
            </button>

            <button 
              className={styles.saveRouteButtonEnabled + " " + styles.saveAsNewRouteButton}
              disabled={tempRouteName.length > 0 ? false : true}
              onClick={() => {
                // Handle save as new route
                onSaveRoute();
                closeSaveRouteDrawer();
              }}
            >
              Save as<br/>New Route
            </button>
          </div>
      </Drawer>
    </div>
  );
};

export default CurrentRoute;